import React, { useState, useEffect } from "react";
import { Typography, Grid } from "@mui/material";
import TextFieldInput from "../../../components/TextFieldInput";
import { useFormik } from "formik";
import UploadInput from "../../../components/UploadInput";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import {
  BOOKING_AS,
  FILE_TYPES_PROPS,
  UPLOAD_TYPE,
} from "../../../config/constant";
import * as yup from "yup";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import { getErrorMessage, getLastStep, removeBlobImages } from "../../../utilis/common-funtions";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  setActiveStep,
  updateCompanyDocuments,
  setEditPreviewApplicant,
  setShowSaveAndPreview
} from "../new-booking-form.slice";
import { route } from "../../../services/apiRoutes";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import Loader from "../../../components/Loader";

const CompanyDocument = () => {
  const { t } = useTranslation();
  const { oppId } = useParams();
  const { activeStep, applicantData, isLoading, entity } = useSelector(
    (state) => state?.newBookingForm
  );
  const history = useHistory()
  const dispatch = useDispatch();
  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);
  const [fieldName, setFieldName] = useState("");
  const [maximumFilesize, setMaximumFilesize] = useState(1);
  const [hideCameraOption, setHideCameraOption] = useState(false);
  const [fileType, setFileType] = useState(UPLOAD_TYPE.PDF);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const companyDocumentFormik = useFormik({
    initialValues: {
      bookingAs: "",
      panNumber: "",
      gstNumber: "",
      companyPANcard: [],
      gstCertificate: [],
      incorporationCertificate: [],
      memorandumofAssociation: [],
      articlesofAssociation: [],
      boardResolution: [],
      partnersResolution: [],
      partnershipDeed: [],
    },
    validationSchema: yup.object().shape({
      panNumber: yup
        .string()
        .matches(
          /^[A-Za-z]{5}\d{4}[A-Za-z]$/,
          t("companyDocument.validations.companyPANInvalid", { entity })
        )
        .required(t("companyDocument.validations.companyPAN", { entity })),

      gstNumber: yup
        .string()
        .matches(
          /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
          t("companyDocument.validations.companyGSTInvalid", { entity })
        )
        .required(t("companyDocument.validations.companyGST", { entity })),

      companyPANcard: yup.array().when("bookingAs", {
        is: (bookingAs) =>
          bookingAs === BOOKING_AS.PARTNERSHIP_FIRM ||
          bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.companyPANcard", { entity }))
            .required(t("companyDocument.validations.companyPANcard", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      gstCertificate: yup.array().when("bookingAs", {
        is: (bookingAs) =>
          bookingAs === BOOKING_AS.PARTNERSHIP_FIRM ||
          bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.gstCertificate", { entity }))
            .required(t("companyDocument.validations.gstCertificate", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      incorporationCertificate: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.incorporationCertificate", { entity }))
            .required(t("companyDocument.validations.incorporationCertificate", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      memorandumofAssociation: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.memorandumofAssociation", { entity }))
            .required(t("companyDocument.validations.memorandumofAssociation", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      articlesofAssociation: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.articlesofAssociation", { entity }))
            .required(t("companyDocument.validations.articlesofAssociation", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      boardResolution: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.CORPORATE,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.boardResolution", { entity }))
            .required(t("companyDocument.validations.boardResolution", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      partnershipDeed: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.PARTNERSHIP_FIRM,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.partnershipDeed", { entity }))
            .required(t("companyDocument.validations.partnershipDeed", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),

      partnersResolution: yup.array().when("bookingAs", {
        is: (bookingAs) => bookingAs === BOOKING_AS.PARTNERSHIP_FIRM,
        then: () =>
          yup
            .array()
            .min(1, t("companyDocument.validations.partnersResolution", { entity }))
            .required(t("companyDocument.validations.partnersResolution", { entity })),
        otherwise: () => yup.array().notRequired(),
      }),
    }),
  });

  useEffect(() => {
    if (applicantData?.bookingAs) {
      companyDocumentFormik.setFieldValue(
        "bookingAs",
        applicantData?.bookingAs
      );
    }
  }, [applicantData]);

  const handleImageValue = (keyName, imgName) => {
    removeBlobImages();
    companyDocumentFormik.setFieldValue(keyName, imgName);
  };

  const handleUpdateImageData = (field, data) => {
    companyDocumentFormik.setFieldValue(field, data);
  };

 const buildPayload = (saveForLater = false) => {
  const documents = {
    pan: companyDocumentFormik?.values?.companyPANcard,
    gstCertificate: companyDocumentFormik?.values?.gstCertificate,
    ...(applicantData?.bookingAs === BOOKING_AS.CORPORATE && {
      incorporationCertificate:
        companyDocumentFormik?.values?.incorporationCertificate,
      memorandumOfAssociation:
        companyDocumentFormik?.values?.memorandumofAssociation,
      articlesOfAssociation:
        companyDocumentFormik?.values?.articlesofAssociation,
      boardResolutionPurchase:
        companyDocumentFormik?.values?.boardResolution,
    }),
    ...(applicantData?.bookingAs === BOOKING_AS.PARTNERSHIP_FIRM && {
      partnersResolutionPurchase:
        companyDocumentFormik?.values?.partnersResolution,
      partnershipDeed: companyDocumentFormik?.values?.partnershipDeed,
    }),
  };

  return {
    opportunityId: oppId,
    lastStep: getLastStep(applicantData?.lastStep, activeStep),
    saveForLater,
    documents,
  };
};


  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleSubmit = async (isSaveandPreview = false) => {
    const errors = await companyDocumentFormik.validateForm();
    if (Object.keys(errors)?.length === 0) {
      try {
        const res = await dispatch(
          updateCompanyDocuments({
            path: route.UPDATE_COMPANY_DOCUMENTS,
            payload: buildPayload(),
            opportunityId: oppId,
          })
        ).unwrap();
        if (res) {
          dispatch(setActiveStep(activeStep + 1));
        }
        dispatch(setShowSaveAndPreview(false))
        if (isSaveandPreview) {
          dispatch(setEditPreviewApplicant(false));
          history.push(`/customer-preview/${oppId}`);
        }
      } catch (error) {
        setSnackbarConfig({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    } else {
      companyDocumentFormik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
    }
  };
  useEffect(() => {
    if (applicantData?.companyDetails) {
      const { documents, gstNumber, companyPan, bookingAs } =
        applicantData.companyDetails;

      companyDocumentFormik.setValues({
        ...companyDocumentFormik.values,
        panNumber: companyPan || "",
        gstNumber: gstNumber || "",
        companyPANcard: documents?.pan || [],
        gstCertificate: documents?.gstCertificate || [],
        incorporationCertificate: documents?.incorporationCertificate || [],
        memorandumofAssociation: documents?.memorandumOfAssociation || [],
        articlesofAssociation: documents?.articlesOfAssociation || [],
        boardResolution: documents?.boardResolutionPurchase || [],
        bookingAs: bookingAs || applicantData?.bookingAs,
        partnershipDeed:documents?.partnershipDeed,
        partnersResolution:documents?.partnersResolutionPurchase
      });
    }
  }, [applicantData]);

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Typography
        sx={{
          color: "#1C252E",
          fontSize: "16px",
          fontWeight: "600",
          lineHeight: "36px",
          mb: "12px",
          textAlign: "center",
        }}
      >
        {t("companyDocument.heading", { entity })}
      </Typography>
      <Grid container spacing={3}>
        {/* pan Number */}
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <TextFieldInput
            field={{
              showCheckIcon: Boolean(companyDocumentFormik.values.panNumber),
              name: "panNumber",
              label: t("companyDocument.labels.companyPANnumber", { entity }),
              placeholder: t("companyDocument.labels.companyPANnumber", { entity }),
              required: true,
              readonly: true,
              value: companyDocumentFormik.values.panNumber,
            }}
            formik={companyDocumentFormik}
          />
        </Grid>
        {/* GST Number */}
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <TextFieldInput
            field={{
              showCheckIcon: Boolean(companyDocumentFormik.values.gstNumber),
              name: "gstNumber",
              label: t("companyDocument.labels.companyGstNumber", { entity }),
              placeholder: t("companyDocument.labels.companyGstNumber", { entity }),
              required: true,
              readonly: true,
              value: companyDocumentFormik.values.gstNumber,
            }}
            formik={companyDocumentFormik}
          />
        </Grid>
        {/* Upload Inputs */}
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <UploadInput
            sx={{ width: "100%" }}
            field={{
              name: "companyPANcard",
              label: t("companyDocument.labels.companyPANcard", { entity }),
              required: true,
              readonly: false,
              placeholder: t("companyDocument.uploadPlaceholders.selectFile", {
                entity,
              }),
              placeholderUpload: t("commonPlaceholders.uploaded"),
              handleOnClick: () => {
                setFieldName("companyPANcard");
                setUploadPopupOpen(true);
                setHideCameraOption(false);
                setMaximumFilesize(1);
                setFileType(UPLOAD_TYPE.PDF_IMAGE);
              },
            }}
            formik={companyDocumentFormik}
            fileType={FILE_TYPES_PROPS.PDF}
            fileSize="1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <UploadInput
            sx={{ width: "100%" }}
            field={{
              name: "gstCertificate",
              label: t("companyDocument.labels.gstCertificate", { entity }),
              required: true,
              readonly: false,
              placeholder: t("companyDocument.uploadPlaceholders.selectFile", {
                entity,
              }),
              placeholderUpload: t("commonPlaceholders.uploaded"),
              handleOnClick: () => {
                setFieldName("gstCertificate");
                setUploadPopupOpen(true);
                setHideCameraOption(false);
                setMaximumFilesize(1);
                setFileType(UPLOAD_TYPE.PDF_IMAGE);
              },
            }}
            formik={companyDocumentFormik}
            fileType={FILE_TYPES_PROPS.PDF_IMAGE}
            fileSize="1"
          />
        </Grid>

        {applicantData?.bookingAs === BOOKING_AS.PARTNERSHIP_FIRM ? (
          <>
                      <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "partnershipDeed",
                  required: true,
                  readonly: false,
                  label: t("companyDocument.labels.partnershipDeed", {
                    entity,
                  }),
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("partnershipDeed");
                    setUploadPopupOpen(true);
                    setHideCameraOption(false);
                    setMaximumFilesize(1);
                    setFileType(UPLOAD_TYPE.PDF_IMAGE);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF_IMAGE}
                fileSize="1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "partnersResolution",
                  label: t("companyDocument.labels.partnersResolution", {
                    entity,
                  }),
                  required: true,
                  readonly: false,
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("partnersResolution");
                    setUploadPopupOpen(true);
                    setHideCameraOption(false);
                    setMaximumFilesize(1);
                    setFileType(UPLOAD_TYPE.PDF_IMAGE);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF_IMAGE}
                fileSize="1"
              />
            </Grid>

          </>
        ) : (
          <>
            {" "}
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "incorporationCertificate",
                  label: t("companyDocument.labels.incorporationCertificate", {
                    entity,
                  }),
                  required: true,
                  readonly: false,
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("incorporationCertificate", { entity });
                    setUploadPopupOpen(true);
                    setHideCameraOption(true);
                    setMaximumFilesize(2);
                    setFileType(UPLOAD_TYPE.PDF);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF}
                fileSize="2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "memorandumofAssociation",
                  label: t("companyDocument.labels.memorandumofAssociation", {
                    entity,
                  }),
                  required: true,
                  readonly: false,
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("memorandumofAssociation");
                    setUploadPopupOpen(true);
                    setHideCameraOption(true);
                    setMaximumFilesize(2);
                    setFileType(UPLOAD_TYPE.PDF);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF}
                fileSize="2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "articlesofAssociation",
                  label: t("companyDocument.labels.articlesofAssociation", {
                    entity,
                  }),
                  required: true,
                  readonly: false,
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("articlesofAssociation");
                    setUploadPopupOpen(true);
                    setHideCameraOption(true);
                    setMaximumFilesize(2);
                    setFileType(UPLOAD_TYPE.PDF);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF}
                fileSize="2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <UploadInput
                sx={{ width: "100%" }}
                field={{
                  name: "boardResolution",
                  label: t("companyDocument.labels.boardResolution", {
                    entity,
                  }),
                  required: true,
                  readonly: false,
                  placeholder: t(
                    "companyDocument.uploadPlaceholders.selectFile",
                    { entity }
                  ),
                  placeholderUpload: t("commonPlaceholders.uploaded"),
                  handleOnClick: () => {
                    setFieldName("boardResolution");
                    setUploadPopupOpen(true);
                    setHideCameraOption(false);
                    setMaximumFilesize(1);
                    setFileType(UPLOAD_TYPE.PDF_IMAGE);
                  },
                }}
                formik={companyDocumentFormik}
                fileType={FILE_TYPES_PROPS.PDF_IMAGE}
                fileSize="1"
              />
            </Grid>
          </>
        )}

        {/* Similarly update other UploadInput fields with `t("labels...")` and `t("uploadPlaceholders...")` */}
      </Grid>

      {uploadPopupOpen && (
        <UploadPopup
          heading={t(`companyDocument.popupPlaceholder.${fieldName}`, { entity })}
          totalImages={1}
          closePopup={() => setUploadPopupOpen(false)}
          handleImageValue={handleImageValue}
          uploadedImage={companyDocumentFormik?.values[fieldName] || []}
          fieldName={fieldName}
          handleUpdateImageData={handleUpdateImageData}
          handlePhysicalImage={() => {}}
          selectedOptionValue={() => {}}
          fileType={fileType}
          hidePhysicalCopyOption
          dynamicSize={maximumFilesize}
          hideCameraOption={hideCameraOption}
        />
      )}
      <CommonButton
        saveDraftPath={route.UPDATE_COMPANY_DOCUMENTS}
        handlePrevious={() => {
          dispatch(setActiveStep(activeStep - 1));
        }}
        handleNext={() => {
          handleSubmit(false);
        }}
        innerSteps={true}
        saveAndPreview={() => {
          handleSubmit(true);
        }}
        saveDraftData={buildPayload(true)}
      />
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default CompanyDocument;
